import React, { useState, useEffect } from 'react';
import { dbBackupsAPI } from '../api';

/**
 * AdminDatabaseBackups — sits inside the Admin → Storage tab. Shows the last
 * 5 mongodump-to-R2 backups (separate bucket, separate scoped token) with
 * size, status, and a download button. "Run backup now" triggers an
 * immediate dump against the live database and uploads it.
 */
const AdminDatabaseBackups = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dbBackupsAPI.list(5);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load backups.');
    } finally {
      setLoading(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setError(null);
    try {
      await dbBackupsAPI.run();
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Backup failed.');
    } finally {
      setRunning(false);
    }
  };

  const download = async (key) => {
    setDownloadingKey(key);
    try {
      const res = await dbBackupsAPI.download(key);
      window.open(res.data.url, '_blank', 'noopener');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not generate download URL.');
    } finally {
      setDownloadingKey(null);
    }
  };

  if (loading) return <SectionSkeleton />;
  if (!data) return null;

  if (data.configured === false) {
    return (
      <Section title="Database backups" subtitle="Not configured.">
        <p style={italicCopy}>
          Daily MongoDB → R2 backups are not enabled on this environment.
          Set <code>R2_BACKUP_BUCKET</code>, <code>R2_BACKUP_ACCESS_KEY_ID</code>,
          {' '}and <code>R2_BACKUP_SECRET_ACCESS_KEY</code> in <code>backend/.env</code>.
        </p>
      </Section>
    );
  }

  const rows = data.rows || [];
  const last = data.last_attempt;
  const lastOk = data.last_successful;
  // Calm rule: if the latest attempt failed but earlier successful backups
  // still exist, we don't show a scary red banner. We show a soft taupe
  // notice that older backups are intact.
  const failedButHaveHistory = last?.status === 'failed' && rows.length > 0;

  return (
    <Section
      title="Database backups"
      subtitle={
        <>
          Daily pure-Python pymongo dump to a <strong>separate R2 bucket</strong> with
          its own scoped token. {data.retention_days}-day retention.
          {last?.status === 'failed' && !failedButHaveHistory && (
            <span style={{ color: '#7a3a3a' }}> Last attempt failed — see error below.</span>
          )}
        </>
      }
      action={
        <button
          type="button"
          data-testid="db-backup-run-btn"
          onClick={runNow}
          disabled={running}
          style={primaryBtn(running)}
        >
          {running ? 'Backing up…' : 'Run backup now'}
        </button>
      }
    >
      {/* Prominent health strip — quickest way to confirm backups are healthy. */}
      <HealthStrip lastOk={lastOk} lastAttempt={last} />

      {failedButHaveHistory && (
        <div data-testid="db-backup-soft-warning" style={softWarningStyle}>
          <p style={softWarningLabel}>Latest attempt failed</p>
          <p style={softWarningCopy}>
            Previous backups remain available — your data is still safe. We'll retry on the next scheduled run.
          </p>
        </div>
      )}

      {error && (
        <div data-testid="db-backup-error" style={errorStyle}>{error}</div>
      )}

      <div data-testid="db-backups-table" style={tableWrap}>
        <div style={tableHead}>
          <span>Date</span>
          <span style={{ textAlign: 'right' }}>Size</span>
          <span>Status</span>
          <span>Trigger</span>
          <span style={{ textAlign: 'right' }}>Download</span>
        </div>

        {rows.length === 0 && (
          <p style={emptyState}>No backups yet — click "Run backup now" to create the first one.</p>
        )}

        {rows.map((r) => (
          <div
            key={r.key}
            data-testid={`db-backup-row-${r.key}`}
            style={tableRow}
          >
            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 13 }}>
              {formatDate(r.created_at)}
            </span>
            <span style={{ textAlign: 'right' }}>{humanBytes(r.size_bytes)}</span>
            <span>
              <StatusPill status={r.status} />
            </span>
            <span style={{ fontSize: 12, color: 'var(--kahla-text-light)' }}>
              {r.triggered_by || 'scheduler'}
            </span>
            <span style={{ textAlign: 'right' }}>
              <button
                type="button"
                data-testid={`db-backup-download-${r.key}`}
                onClick={() => download(r.key)}
                disabled={downloadingKey === r.key}
                style={ghostBtn(downloadingKey === r.key)}
              >
                {downloadingKey === r.key ? '…' : 'Download'}
              </button>
            </span>
          </div>
        ))}
      </div>

      {last?.status === 'failed' && last.error && !failedButHaveHistory && (
        <p style={{ ...italicCopy, marginTop: 14, color: '#7a3a3a' }}>
          <strong>Last error:</strong> {last.error}
        </p>
      )}
    </Section>
  );
};

// ============================================================
// Sub-components & helpers
// ============================================================

const HealthStrip = ({ lastOk, lastAttempt }) => {
  // Healthy: there's a recent success AND the latest attempt didn't fail.
  const failedSinceOk = lastAttempt && lastAttempt.status === 'failed';
  const healthy = !!lastOk && !failedSinceOk;

  if (!lastOk) {
    return (
      <div data-testid="backup-health-strip" style={healthStripStyle('warn')}>
        <Dot tone="warn" />
        <div>
          <p style={healthLabel}>No successful backup yet</p>
          <p style={healthDetail}>
            Click "Run backup now" to create the first one — backups run automatically every 24h after that.
          </p>
        </div>
      </div>
    );
  }

  // If a previous successful backup still exists, even a failed latest attempt
  // is non-critical — surface the green/ok tone and let the soft-warning band
  // carry the nuance. Keeps the page calm and avoids alarming the founder.
  const tone = healthy ? 'ok' : 'soft';
  const when = formatDate(lastOk.finished_at);
  const ago = relativeAgo(lastOk.finished_at);
  return (
    <div data-testid="backup-health-strip" data-status={tone} style={healthStripStyle(tone)}>
      <Dot tone={tone} />
      <div style={{ minWidth: 0 }}>
        <p style={healthLabel}>Last successful backup</p>
        <p style={healthDetail}>
          {when} <span style={{ color: 'rgba(74,69,64,0.5)' }}>· {ago}</span>
          {' · '}
          <span style={{ fontFamily: 'inherit' }}>{humanBytes(lastOk.size_bytes)}</span>
          {lastOk.triggered_by ? <span style={{ color: 'rgba(74,69,64,0.5)' }}> · by {lastOk.triggered_by}</span> : null}
        </p>
      </div>
    </div>
  );
};

const Dot = ({ tone }) => {
  const palette = tone === 'ok'
    ? '#5a7a3a'
    : tone === 'soft'
      ? '#a8946b'
      : tone === 'warn'
        ? '#cf9a3d'
        : '#b35a4a';
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 10, height: 10, borderRadius: '50%',
        background: palette,
        boxShadow: `0 0 0 4px ${palette}22`,
        flexShrink: 0,
        marginTop: 6,
      }}
    />
  );
};

function relativeAgo(iso) {
  if (!iso) return '';
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  } catch (e) { return ''; }
}

const Section = ({ title, subtitle, action, children }) => (
  <section
    data-testid="admin-db-backups-section"
    style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 36 }}
  >
    <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap' }}>
      <div>
        <p style={kicker}>Database</p>
        <h2 style={titleStyle}>{title}</h2>
        {subtitle && <p style={italicCopy}>{subtitle}</p>}
      </div>
      {action}
    </header>
    {children}
  </section>
);

const StatusPill = ({ status }) => {
  const palette = status === 'failed'
    ? { bg: '#f5e3df', fg: '#7a3a3a' }
    : { bg: '#e9efe2', fg: '#3f5c2e' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      fontSize: 10,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      background: palette.bg,
      color: palette.fg,
    }}>
      {status === 'failed' ? 'failed' : 'success'}
    </span>
  );
};

const SectionSkeleton = () => (
  <section style={{ marginTop: 36, padding: 40 }}>
    <div className="spinner" />
  </section>
);

function humanBytes(n) {
  n = Math.max(parseInt(n) || 0, 0);
  if (n === 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${i === 0 ? n : n.toFixed(1)} ${units[i]}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (e) { return iso; }
}

// ============================================================
// Styles
// ============================================================
const kicker = {
  fontFamily: 'Playfair Display, serif', fontSize: 11,
  letterSpacing: '0.32em', textTransform: 'uppercase',
  color: 'var(--kahla-text-light)', margin: '0 0 10px', fontWeight: 300,
};
const titleStyle = {
  fontFamily: 'Playfair Display, serif', fontSize: 24, fontWeight: 400,
  color: 'var(--kahla-charcoal)', margin: '0 0 8px', lineHeight: 1.2,
};
const italicCopy = {
  fontFamily: 'Cormorant Garamond, Playfair Display, serif',
  fontStyle: 'italic',
  fontSize: 15, color: 'var(--kahla-text-light)', margin: 0, lineHeight: 1.6, maxWidth: 720,
};
const primaryBtn = (disabled) => ({
  background: 'var(--kahla-charcoal)', color: '#fff', border: 'none',
  padding: '11px 22px',
  fontFamily: 'inherit', fontSize: 11, letterSpacing: '0.16em',
  textTransform: 'uppercase',
  cursor: disabled ? 'wait' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});
const ghostBtn = (disabled) => ({
  background: 'transparent', color: 'var(--kahla-charcoal)',
  border: '1px solid var(--kahla-border)', padding: '6px 14px',
  fontFamily: 'inherit', fontSize: 10, letterSpacing: '0.18em',
  textTransform: 'uppercase',
  cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? 0.6 : 1,
});
const tableWrap = { border: '1px solid var(--kahla-border)', background: '#fff' };
const tableHead = {
  display: 'grid',
  gridTemplateColumns: '1.6fr 0.7fr 0.7fr 1fr 0.8fr',
  alignItems: 'center', gap: 0,
  padding: '14px 18px', borderBottom: '1px solid var(--kahla-cream)',
  fontFamily: 'Playfair Display, serif',
  fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
  color: 'var(--kahla-text-light)',
};
const tableRow = {
  display: 'grid',
  gridTemplateColumns: '1.6fr 0.7fr 0.7fr 1fr 0.8fr',
  alignItems: 'center', gap: 0,
  padding: '14px 18px',
  borderBottom: '1px solid var(--kahla-cream)',
  fontSize: 13, color: 'var(--kahla-charcoal)',
};
const emptyState = {
  padding: 30, textAlign: 'center',
  fontFamily: 'Cormorant Garamond, Playfair Display, serif',
  fontStyle: 'italic', color: 'var(--kahla-text-light)',
};
const errorStyle = {
  background: '#f8eee9', color: '#7a3a3a', padding: '10px 14px',
  fontSize: 13, fontStyle: 'italic',
};

const softWarningStyle = {
  background: '#f6f1e8',
  border: '1px solid #d9c9a8',
  padding: '12px 16px',
  marginTop: 14,
};
const softWarningLabel = {
  margin: 0,
  fontFamily: 'Playfair Display, serif',
  fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
  color: '#7a6a4a',
};
const softWarningCopy = {
  margin: '4px 0 0',
  fontSize: 14,
  color: 'var(--kahla-charcoal)',
  fontFamily: 'Cormorant Garamond, Playfair Display, serif',
  fontStyle: 'italic',
  lineHeight: 1.55,
};

const healthStripStyle = (tone) => {
  const map = {
    ok:    { bg: '#f3f5ee', border: '#d4dfc4' },
    soft:  { bg: '#f6f1e8', border: '#d9c9a8' },
    warn:  { bg: '#fbf5e8', border: '#e5c97a' },
    error: { bg: '#f5e3df', border: '#c8a39a' },
  };
  const c = map[tone] || map.ok;
  return {
    display: 'flex', alignItems: 'flex-start', gap: 14,
    padding: '14px 18px',
    background: c.bg,
    border: `1px solid ${c.border}`,
  };
};
const healthLabel = {
  margin: 0,
  fontFamily: 'Playfair Display, serif',
  fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
  color: 'var(--kahla-text-light)',
};
const healthDetail = {
  margin: '4px 0 0',
  fontSize: 14,
  color: 'var(--kahla-charcoal)',
  fontFamily: 'Playfair Display, serif',
};

export default AdminDatabaseBackups;
